import Vue from 'vue'
import Vuex from 'vuex'

import cache from './lib/cache'
import ZitatAPI from './lib/api'

import config from './config.js'

Vue.use(Vuex)

const CACHE_KEYS = ['token', 'books', 'quotes', 'settings', 'user']

const store = new Vuex.Store({
  state: {
    initialized: false,
    updating: false,
    syncing: false,
    updateReady: false,
    token: null,
    books: [],
    quotes: [],
    user: null,
    settings: {},
    online: navigator.onLine,
    bookOldIdMapping: {},
    quoteOldIdMapping: {},
    imageFile: null,
    deletedQuotes: [],
    deletedBooks: []
  },
  getters: {
    loggedIn: state => {
      return !!state.token
    },
    userName: state => {
      if (state.user === null) {
        return ''
      }
      return state.user.name
    },
    canSync: state => {
      return !!state.token && state.online
    },
    api: state => {
      return new ZitatAPI(
        config.API_URL,
        state.token
      )
    },
    getBookById: (state, getters) => (id) => {
      return state.books[getters.bookMap[id]]
    },
    getBookIdByOldId: (state, getters) => (oldId) => {
      return state.bookOldIdMapping[oldId]
    },
    getQuoteById: (state, getters) => (id) => {
      return state.quotes[getters.quoteMap[id]]
    },
    getQuoteIdByOldId: (state, getters) => (oldId) => {
      return state.quoteOldIdMapping[oldId]
    },
    getQuotesByBook: (state) => (id) => {
      return state.quotes.filter(q => q.book && q.book.id === id)
    },
    getQuotesByTag: (state) => (tag) => {
      return state.quotes.filter(q => q.tags.includes(tag))
    },
    unsyncedBooks: state => {
      return state.books
        .filter((b) => (b.id && b.id[0] === '@') || b.unsynced)
        .concat(state.deletedBooks)
    },
    unsyncedQuotes: state => {
      return state.quotes
        .filter((q) => (q.id && q.id[0] === '@') || q.unsynced)
        .concat(state.deletedQuotes)
    },
    needsSync: (state, getters) => {
      return getters.unsyncedBooks.length > 0 || getters.unsyncedQuotes.length > 0
    },
    quoteBooks: (state, getters) => {
      return state.books.filter((b) => !!getters.bookQuoteMap[b.id])
    },
    bookMap: state => {
      const m = {}
      state.books.forEach((b, i) => {
        m[b.id] = i
      })
      return m
    },
    bookQuoteMap: state => {
      const m = {}
      state.quotes.forEach((q) => {
        if (q.book) {
          m[q.book.id] = m[q.book.id] || []
          m[q.book.id].push(q.id)
        }
      })
      return m
    },
    quoteMap: state => {
      const m = {}
      state.quotes.forEach((b, i) => {
        m[b.id] = i
      })
      return m
    },
    allTags (state) {
      const set = {}
      const tags = []
      state.quotes.forEach((q) => {
        q.tags.forEach((t) => {
          if (!set[t]) {
            set[t] = true
            tags.push(t)
          }
        })
      })
      return tags
    }
  },
  mutations: {
    loadFromCache (state, cached) {
      if (cached) {
        Object.keys(cached).forEach((key) => {
          if (CACHE_KEYS.includes(key)) {
            Vue.set(state, key, cached[key] || state[key])
          }
        })
      }
      state.initialized = true
    },
    storeToken (state, token) {
      state.token = token
    },
    setUser (state, user) {
      state.user = user
    },
    login (state, token) {
      state.token = token
    },
    setBooks (state, books) {
      state.books = books
    },
    setQuotes (state, quotes) {
      state.quotes = quotes
    },
    setOnline (state, online) {
      state.online = online
    },
    updateReady (state, update) {
      state.updateReady = update
    },
    setSyncing (state, syncing) {
      state.syncing = syncing
    },
    setUpdating (state, updating) {
      state.updating = updating
    },
    addQuote (state, quote) {
      if (!quote.id) {
        quote.id = '@' + Math.random()
      }
      state.quotes = [
        quote,
        ...state.quotes
      ]
    },
    updateQuote (state, quote) {
      let index = state.quotes.findIndex(q => q.id === quote.id)
      state.quotes = [
        ...state.quotes.slice(0, index),
        quote,
        ...state.quotes.slice(index + 1)
      ]
    },
    deleteQuote (state, quote) {
      state.quotes = state.quotes.filter(q => q.id !== quote.id)
    },
    replaceQuote (state, { oldId, newQuote }) {
      Vue.set(state.quoteOldIdMapping, oldId, newQuote.id)
      state.quotes = state.quotes.map(q => q.id === oldId ? newQuote : q)
    },
    updateBook (state, book) {
      if (!book) {
        return
      }
      if (book && !book.id) {
        book.id = '@' + Math.random()
      }
      let index = state.books.findIndex(b => b.id === book.id)
      state.books = [
        ...state.books.slice(0, index),
        book,
        ...state.books.slice(index + 1)
      ]
    },
    replaceBook (state, { oldId, newBook }) {
      Vue.set(state.bookOldIdMapping, oldId, newBook.id)
      state.quotes = state.quotes.map(q => {
        if (q.book && q.book.id === oldId) {
          q.book = newBook
          return q
        }
        return q
      })
      state.books = state.books.map(b => b.id === oldId ? newBook : b)
    },
    logout (state) {
      state.user = null
      state.token = null
    },
    setFile (state, file) {
      state.imageFile = file
    },
    syncComplete (state) {
      state.deletedQuotes = []
      state.deletedBooks = []
    }
  },
  actions: {
    storeToken ({ commit }, token) {
      commit('storeToken', token)
    },
    sync ({ state, commit, getters }) {
      if (!getters.canSync || !getters.needsSync) {
        return Promise.resolve(false)
      }
      commit('setSyncing', true)
      const bookPromises = getters.unsyncedBooks.map(b => new Promise((resolve, reject) => {
        let promise
        if (b.delete) {
          promise = getters.api.deleteBook(b)
        } else if (b.id[0] === '@') {
          const oldId = b.id
          delete b.id
          promise = getters.api.addBook(b).then((newBook) => {
            commit('replaceBook', { oldId, newBook })
          })
        } else {
          promise = getters.api.updateBook(b)
        }
        return promise.then(resolve)
      }))
      Promise.all(bookPromises).then(() => {
        const quotePromises = getters.unsyncedQuotes.map(q => new Promise((resolve, reject) => {
          let promise
          if (q.delete) {
            promise = getters.api.deleteQuote(q)
          } else if (q.id[0] === '@') {
            const oldId = q.id
            const quoteCopy = { ...q }
            delete quoteCopy.id
            promise = getters.api.addQuote(quoteCopy).then(newQuote => {
              commit('replaceQuote', { oldId, newQuote })
            })
          } else {
            promise = getters.api.updateQuote(q)
          }
          return promise.then(resolve)
        }))
        return Promise.all(quotePromises)
      }).then(() => {
        commit('setSyncing', false)
        commit('syncComplete')
      }).catch(() => {
        commit('storeToken', null)
        commit('setSyncing', false)
      })
    },
    updateFromRemote ({ commit, state, getters }) {
      if (getters.canSync) {
        commit('setUpdating', true)
        return Promise.all([
          getters.api.getUser(),
          getters.api.getAllQuotes(),
          getters.api.getAllBooks()
        ]).then(([user, quotes, books]) => {
          commit('setUser', user)
          commit('setQuotes', quotes)
          commit('setBooks', books)
        }).then(() => {
          commit('setUpdating', false)
          return true
        }).catch(() => {
          commit('storeToken', null)
          commit('setUpdating', false)
        })
      }
      return Promise.resolve()
    },
    addQuote ({ commit, state, getters }, quote) {
      if (getters.canSync) {
        commit('setSyncing', true)
        return getters.api.addQuote(quote)
          .then((result) => {
            commit('updateBook', result.book)
            commit('addQuote', result)
            commit('setSyncing', false)
            return result
          })
          // TODO: .catch()
      }
      commit('addQuote', quote)
      return Promise.resolve(quote)
    },
    updateQuote ({ commit, state, getters }, quote) {
      if (getters.canSync) {
        commit('setSyncing', true)
        return getters.api.updateQuote(quote)
          .then((result) => {
            commit('updateBook', result.book)
            commit('updateQuote', result)
            commit('setSyncing', false)
            return result
          })
          // TODO: .catch()
      }
      quote.unsynced = true
      commit('updateQuote', quote)
      return Promise.resolve(quote)
    },
    deleteQuote ({ commit, state, getters }, quote) {
      if (quote.id[0] !== '@') {
        if (getters.canSync) {
          return getters.api.deleteQuote(quote)
            .then((result) => {
              commit('deleteQuote', quote)
            })
            // TODO: .catch()
        }
        state.deletedQuotes = [
          ...state.deletedQuotes,
          { id: quote.id, delete: true }
        ]
      }
      commit('deleteQuote', quote)
    },
    updateBooks ({ commit, dispatch, getters }) {
      if (getters.canSync) {
        commit('setSyncing', true)
        return getters.api.updateBooks()
          .then((result) => {
            dispatch('updateFromRemote').then(() => {
              commit('setSyncing', false)
            })
          })
          // TODO: .catch()
      }
    },
    login ({ commit, dispatch, getters }, credentials) {
      return getters.api.login(credentials)
        .then((token) => {
          commit('login', token)
          return dispatch('sync').then(() => dispatch('updateFromRemote'))
        })
    },
    logout ({ commit, getters }) {
      if (getters.loggedIn) {
        return this.getters.api.logout()
          .then(() => {
            commit('logout')
          })
      }
    },
    changePassword ({ getters }, data) {
      return getters.api.changePassword(data)
    }
  },
  plugins: [cache]
})

export default store
